import { useCallback, useContext, useMemo } from 'react';
import { tryConvertUIUrvalToTEDUrval } from '../../../../../../../Components/Urval/Utils/convertUIUrvalToTEDUrval';
import EkonomiskAnalysQuery, {
  IAnalys,
} from '../../Queries/EkonomiskAnalysQuery/EkonomiskAnalysQuery';
import { getForceHelarsJamforelse } from '../../Utils/userInfoUtils';
import { IColumn } from '../EkonomiskAnalysTable/EkonomiskAnalysTableBase';
import {
  IFilledLocalAnalys,
  ILocalAnalys,
} from '../EkonomiskAnalysMain/EkonomiskAnalysMain';
import {
  ISelectedAnalys,
  ISelectedAnalysCodePrivilege,
} from '../../EkonomiskAnalysPage';
import { EkonomiskAnalysContext } from '../Context/EkonomiskAnalysContext';
import { AnalysInitQuery_logo } from '../../Queries/EkonomiskAnalysInitQuery/_QueryTypes/AnalysInitQuery';
import { datesToTimeInterval } from '../../Utils/convertUtils';
import { getSelectChoiceNameFromValue } from '../../../../../../../BaseComponents/Selects/SelectCommons';
import { Denominations } from '../EkonomiskAnalysDenominations/EkonomiskAnalysDenominations';
import { getComparisonString } from '../../Utils/analysUtils';
import {
  AnalysLevelEnum,
  AnalysTypeEnum,
  PrevColumnTypeEnum,
  SpecifikationTypeEnum,
} from '../../Const/EkonomiskAnalysConsts';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import { SammanfattningSubLevelTable } from './SammanfattningSubLevelTable';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import {
  extendRowsWithAvvikelser,
  filterDataWithValuesForCurrentYear,
  getColumnHeaders,
  getInnerCellData,
} from '../../Utils/detaljanalysUtils';
import { SammanfattningDetails } from './SammanfattningDetails';
import EkonomiskAnalysSammanfattningCharts from './Components/EkonomiskAnalysSammanfattningCharts/EkonomiskAnalysSammanfattningCharts';
import { SammanfattningInsikterRekommendationer } from './SammanfattningInsikterRekommendationer';
import { IEkonomiskAnalysFilledDates } from '../AnalysSettingsHandler/AnalysSettingsHandler';
import { ISelection } from '../../Queries/EkonomiskAnalysInitQuery/EkonomiskAnalysInitQuery';
import { IPrintSections } from './DetaljanalysPrintModal';
import aiActiveQuery from './Querys/EkonomiskAnalysDetaljAiActive.gql';
import { useQuery } from '@apollo/client';
import {
  EkonomiskAnalysDetaljAiActive,
  EkonomiskAnalysDetaljAiActiveVariables,
} from './Querys/_QueryTypes/EkonomiskAnalysDetaljAiActive';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';

export interface ISammanfattningsdata {
  rapportInfo: ISammanfattningOrgInfo | null;
  kolumnerRubriker: IColumn[];
  kostnadsData: IRows;
  selectedRowHeader?: string;
}

export interface IRows {
  nextToLastRow?: ISammanfattningRows[];
  lastRow: ISammanfattningRows[];
}

export interface ISammanfattningRowPrevPeriod {
  type: PrevColumnTypeEnum;
  specifikation: SpecifikationTypeEnum;
  value: number;
  period: string;
  periodNumber: number;
  periodYear: number;
  title: string;
}
export interface ISammanfattningRows {
  id: string;
  currentLevel: string;
  header: string;
  utgaendeVarde: number;
  utgaendeVardeLastYear: number;
  utgaendeVardePeriodLastYear: number;
  budgetArVarde: number;
  budgetRiktvarde: number;
  avvikelseBudgetRiktvarde: number;
  avvikelseProcentBudgetRiktvarde: string;
  helarsPrognos: number;
  status: string;
  prevPeriods: ISammanfattningRowPrevPeriod[];
}

interface ISammanfattningOrgInfo {
  analysName?: string;
  organisationName?: string;
  kodbehorighetNamn?: string;
  analysPeriod?: string;
  enhet?: string;
  jamforelseTal?: string;
}

interface IProps {
  availableReports: ISelection[];
  analysType: AnalysTypeEnum;
  impersonatedCodePrivilege?: ISelectedAnalysCodePrivilege;
  logo: AnalysInitQuery_logo;
  settingsForCurrentlyShown: IFilledLocalAnalys;
  updatedSettings: ILocalAnalys;
  selectedAnalys: ISelectedAnalys;
  settingsChanged: boolean;
  setRefetchAnalys: (refetchAnalys: () => void) => void;
  stopTableRendering: boolean;
  analysLevel: AnalysLevelEnum;
  originalSettings: IFilledLocalAnalys;
  loading: boolean;
  // moved states from Sammanfattning to parent
  expandedDetails: boolean;
  setExpandedDetails: (value: boolean) => void;
  expandedTable: boolean;
  setExpandedTable: (value: boolean) => void;
  expandedAIInsights: boolean;
  setExpandedAIInsights: (value: boolean) => void;
  expandedBarChart: boolean;
  setExpandedBarChart: (value: boolean) => void;
  expandedLineChart: boolean;
  setExpandedLineChart: (value: boolean) => void;
  expandedPieChart: boolean;
  setExpandedPieChart: (value: boolean) => void;
  expandedResultatDiagram: boolean;
  setExpandedResultatDiagram: (value: boolean) => void;
  printSections: IPrintSections;
  showFordelningDiagram: boolean;
  showResultatDiagram: boolean;
}

export const EkonomiskAnalysSammanfattning = (props: IProps) => {
  const {
    userInfo,
    harPeriodiseradBudget,
    kodbehorighetsUrval,
    hasWholeCodeBook,
  } = useContext(EkonomiskAnalysContext);
  const { activeDatabase } = useContext(ActiveDatabaseContext);

  const aiActiveQueryResult = useQuery<
    EkonomiskAnalysDetaljAiActive,
    EkonomiskAnalysDetaljAiActiveVariables
  >(aiActiveQuery, {
    variables: {
      dbname: activeDatabase ?? '',
    },
  });

  const promptForAI = useMemo(() => {
    return props.updatedSettings.ai_prompt
      ? props.updatedSettings.ai_prompt
      : '';
  }, [props.updatedSettings.ai_prompt]);

  const getSammanfattningColumns = useCallback((data: IAnalys) => {
    const sumColumns: IColumn[] = data.columns.filter(col =>
      col.fieldType.includes('sum')
    );

    const dataColumns: IColumn[] = data.columns.filter(col =>
      col.fieldType.includes('col')
    );

    const finalColumnData: IColumn[] = [...sumColumns, ...dataColumns];

    return finalColumnData;
  }, []);

  const getSammanfattningRows = useCallback(
    (
      data: IAnalys,
      analysLevel: AnalysLevelEnum,
      analysPeriod: IEkonomiskAnalysFilledDates,
      currentLevel: string,
      columns: IColumn[]
    ): IRows => {
      if (!data || !data.data || data.data.length === 0) return { lastRow: [] };

      const rows: ISammanfattningRows[] = [];
      let resultRows: IRows = { lastRow: [] };
      if (analysLevel === AnalysLevelEnum.UNSET) {
        data.data.forEach(row => {
          let result: any = {};
          result = getInnerCellData(
            row,
            currentLevel,
            columns,
            analysPeriod.from.year
          );
          if (result !== null) {
            rows.push(result);
          }
          resultRows = { lastRow: rows };
        });
      } else if (analysLevel === AnalysLevelEnum.MIDDLE) {
        data.data[0].children?.forEach(childRow => {
          let result: any = {};
          result = getInnerCellData(
            childRow,
            currentLevel,
            columns,
            analysPeriod.from.year
          );
          if (result !== null) {
            rows.push(result);
          }
        });
        resultRows = { lastRow: rows };
      } else if (analysLevel === AnalysLevelEnum.LAST) {
        const kontoGrupp: ISammanfattningRows[] = [];
        const baskonto: ISammanfattningRows[] = [];

        data.data[0].children?.forEach(childRow => {
          let kontogruppResult: any = {};
          let baskontoResult: any = {};
          kontogruppResult = getInnerCellData(
            childRow,
            currentLevel,
            columns,
            analysPeriod.from.year
          );
          if (kontogruppResult !== null) {
            kontoGrupp.push(kontogruppResult);
          }

          childRow?.children?.forEach(grandchild => {
            baskontoResult = getInnerCellData(
              grandchild,
              currentLevel,
              columns,
              analysPeriod.from.year
            );
            if (baskontoResult !== null) {
              baskonto.push(baskontoResult);
            }
            //Måste återställa objektet mellan varje baskonto så att vi det nya inte ersätts av det gamla värdet
            baskontoResult = {};
          });
        });

        resultRows = { nextToLastRow: kontoGrupp, lastRow: baskonto };
      }

      resultRows = extendRowsWithAvvikelser(
        resultRows,
        analysPeriod.from.month,
        props.analysType
      );

      return resultRows;
    },
    [props.analysType]
  );

  const getOrgInformation = useCallback(
    (
      logo: AnalysInitQuery_logo,
      kodbehorighetNamn: string | undefined,
      analysPeriod: string,
      enhet: string,
      jamforelseTal: string
    ): ISammanfattningOrgInfo | null => {
      let hasValue = false;
      let result: ISammanfattningOrgInfo = {};

      if (logo && logo.namn) {
        result.organisationName = logo.namn;
        hasValue = true;
      }

      if (kodbehorighetNamn) {
        result.kodbehorighetNamn = kodbehorighetNamn;
        hasValue = true;
      }

      if (analysPeriod) {
        result.analysPeriod = analysPeriod;
        hasValue = true;
      }

      if (enhet) {
        result.enhet = enhet;
        hasValue = true;
      }

      if (jamforelseTal) {
        result.jamforelseTal = jamforelseTal;
        hasValue = true;
      }

      if (hasValue) return result;

      return null;
    },
    []
  );

  const getEnhet = useCallback((): string => {
    const denomination = getSelectChoiceNameFromValue(
      props.updatedSettings.other.denomination,
      Denominations
    );
    return denomination;
  }, [props.updatedSettings.other.denomination]);

  const getHeaderLevel = useCallback(
    (data: IAnalys, analysLevel: AnalysLevelEnum): string | undefined => {
      if (
        analysLevel !== AnalysLevelEnum.UNSET &&
        data?.data[0]?.data[0]?.value
      ) {
        const headerData = data.data[0].data[0].value;
        return headerData;
      }
      return undefined;
    },
    []
  );

  const kodbehorighetNamn = useMemo(() => {
    const kodbehorighetNamn =
      kodbehorighetsUrval && kodbehorighetsUrval.savedUrvalProps
        ? kodbehorighetsUrval.savedUrvalProps.namn
        : undefined;
    return kodbehorighetNamn;
  }, [kodbehorighetsUrval]);

  const analysPeriod = useMemo(() => {
    const dateInterval = datesToTimeInterval(
      props.settingsForCurrentlyShown.dates
    );
    const formattedFromMonth = dateInterval.fromMonth
      ? dateInterval.fromMonth.toString().padStart(2, '0')
      : '??';
    const formatttedToMonth = dateInterval.toMonth
      ? dateInterval.toMonth.toString().padStart(2, '0')
      : '??';

    const result = `${dateInterval.fromYear}-${formattedFromMonth} till ${dateInterval.toYear}-${formatttedToMonth}`;
    return result;
  }, [props.settingsForCurrentlyShown.dates]);

  const jamforelseTal = useMemo(() => {
    const comparision = getComparisonString(
      props.settingsForCurrentlyShown.dates,
      props.settingsForCurrentlyShown.isInterval,
      props.settingsForCurrentlyShown.dateInputType,
      userInfo?.preferHelarsjamforelse
    );
    return comparision;
  }, [
    props.settingsForCurrentlyShown.dateInputType,
    props.settingsForCurrentlyShown.dates,
    props.settingsForCurrentlyShown.isInterval,
    userInfo?.preferHelarsjamforelse,
  ]);

  const getFullAnalysData = useCallback(
    (
      data: IAnalys,
      analysLevel: AnalysLevelEnum,
      dates: IEkonomiskAnalysFilledDates
    ): ISammanfattningsdata | undefined => {
      if (
        !data ||
        !data.data ||
        data.data.length === 0 ||
        !data.columns ||
        data.columns.length === 0
      )
        return undefined;

      let analysDataWithCurrentYearValues: IAnalys = data;

      if (data.data.length > 1) {
        // Om det finns flera övergripande rader (toplevel), filtrera bort de som inte har något värde för nuvarande år (utgående värde och budget för nuvarande år)
        const selectedYear = dates.from.year;
        const filteredRows = filterDataWithValuesForCurrentYear(
          data,
          selectedYear
        );
        if (filteredRows.length === 1) {
          analysDataWithCurrentYearValues = {
            ...analysDataWithCurrentYearValues,
            data: filteredRows,
          };
        }
      }

      const columns = getSammanfattningColumns(analysDataWithCurrentYearValues);
      const selectedHeaderBenamning = getHeaderLevel(
        analysDataWithCurrentYearValues,
        analysLevel
      );
      const reportsums = getColumnHeaders(columns);
      const currentLevel = reportsums[reportsums.length - 1];
      const rows = getSammanfattningRows(
        analysDataWithCurrentYearValues,
        analysLevel,
        props.settingsForCurrentlyShown.dates,
        currentLevel,
        columns
      );
      const enhet = getEnhet();
      const orgInformation = getOrgInformation(
        props.logo,
        kodbehorighetNamn,
        analysPeriod,
        enhet,
        jamforelseTal
      );

      const result = {
        rapportInfo: orgInformation,
        kolumnerRubriker: columns,
        kostnadsData: rows,
        selectedRowHeader: selectedHeaderBenamning,
      };
      return result;
    },
    [
      analysPeriod,
      getEnhet,
      getHeaderLevel,
      getOrgInformation,
      getSammanfattningColumns,
      getSammanfattningRows,
      jamforelseTal,
      kodbehorighetNamn,
      props.logo,
      props.settingsForCurrentlyShown.dates,
    ]
  );

  const currentCodePrivilegeId = useMemo(() => {
    return props.impersonatedCodePrivilege
      ? props.impersonatedCodePrivilege.id
      : props.selectedAnalys.codePrivilege.id;
  }, [props.impersonatedCodePrivilege, props.selectedAnalys.codePrivilege]);

  const changedCodePrivilege = useMemo(() => {
    return props.impersonatedCodePrivilege
      ? props.impersonatedCodePrivilege.id !==
          props.selectedAnalys.codePrivilege.id
      : false;
  }, [props.impersonatedCodePrivilege, props.selectedAnalys.codePrivilege]);

  const firstVisibleSection = useMemo(() => {
    const sectionOrder: (keyof IPrintSections)[] = [
      'sammanfattning',
      'resultatdiagram',
      'linjediagram',
      'cirkeldiagram',
      'stapeldiagram',
      'tabell',
      'aiInsikter',
    ];

    for (let i = 0; i < sectionOrder.length; i++) {
      const section = sectionOrder[i];
      if (props.printSections[section]) {
        return section;
      }
    }

    return null;
  }, [props.printSections]);

  return (
    <EkonomiskAnalysQuery
      fetchPolicy="cache-first"
      name={props.settingsForCurrentlyShown.name}
      title={props.settingsForCurrentlyShown.title}
      category={props.settingsForCurrentlyShown.category}
      columns={props.settingsForCurrentlyShown.columns.map(x => x.data)}
      sums={props.settingsForCurrentlyShown.sums}
      dates={props.settingsForCurrentlyShown.dates}
      visaSummeringar={props.settingsForCurrentlyShown.visaSummeringar}
      codePrivilegesId={currentCodePrivilegeId}
      baseUrvalId={
        props.selectedAnalys.analys.grundurval
          ? props.selectedAnalys.analys.grundurval.id
          : undefined
      }
      urval={tryConvertUIUrvalToTEDUrval(props.updatedSettings.urval)}
      endastBokfort={
        !props.settingsForCurrentlyShown.other.includeEjBokfordaJournaler
      }
      forceHelarsjamforelse={getForceHelarsJamforelse(
        harPeriodiseradBudget,
        userInfo?.preferHelarsjamforelse
      )}
      detailedSelection={tryConvertUIUrvalToTEDUrval(
        props.settingsForCurrentlyShown.detaljUrval
      )}
      settingsChanged={props.settingsChanged}
      setRefetchAnalys={props.setRefetchAnalys}
      skip={false}
      includeForcalculatedColumns={true}
    >
      {data => {
        const analysData = getFullAnalysData(
          data,
          props.analysLevel,
          props.settingsForCurrentlyShown.dates
        );

        if (props.loading || !analysData) {
          return <EDAvenyProgress />;
        }

        const rubrikNiva = analysData?.kolumnerRubriker[0]?.title1;
        const nrOfSublevels = analysData?.kolumnerRubriker.filter(column =>
          column.fieldType.includes('sum')
        ).length;
        const subLevelNiva = nrOfSublevels
          ? analysData.kolumnerRubriker[nrOfSublevels - 1]?.title1
          : undefined;

        const tableRows = analysData?.kostnadsData.lastRow;
        return (
          <>
            {props.loading || data.data.length === 0 ? (
              <EDAvenyProgress />
            ) : (
              <EDBox
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  mt: 0,
                  //Här går det nog att targeta bättre med css för att undvika att behöva !important
                  '@media print': {
                    '@page': {
                      margin: '1cm',
                      size: 'A4 landscape',
                    },

                    '& .print-hidden': {
                      display: 'none !important',
                    },

                    '& .MuiPaper-root': {
                      border: 'none',
                      boxShadow: 'none',
                      maxWidth: '100% !important',
                      minWidth: '0 !important',
                    },

                    '& .MuiBox-root': {
                      minWidth: '0 !important',
                    },

                    '&. MuiAccordionSummary-content ': {
                      margin: '0 !important',
                    },

                    '& .MuiAccordionSummary-root': {
                      minHeight: 'auto !important',
                      padding: '8px 16px !important',
                      display: 'flex !important',
                      // marginBottom: '8px !important',
                      margin: 0,

                      '& .MuiSvgIcon-root': {
                        display: 'none !important',
                      },
                    },

                    '& .MuiAccordionDetails-root': {
                      display: 'block',
                      padding: '16px',
                    },

                    '& .print-section': {
                      marginBottom: '16px',
                      pageBreakAfter: 'auto',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                    },

                    // All visible sections get page breaks by default
                    '& .print-section:not(.print-hidden)': {
                      pageBreakBefore: 'always',
                    },

                    // Except the first visible section
                    '& .print-section.first-section': {
                      pageBreakBefore: 'auto !important',
                    },

                    '& .print-section .MuiBox-root': {
                      overflow: 'visible !important',
                      maxHeight: 'none !important',
                      height: 'auto !important',
                      maxWidth: '100% !important',
                      width: '100% !important',
                      minWidth: '0 !important',
                    },
                  },
                }}
              >
                <EDBox
                  data-print-section="sammanfattning"
                  className={`print-section ${
                    !props.printSections.sammanfattning ? 'print-hidden' : ''
                  } ${
                    firstVisibleSection === 'sammanfattning'
                      ? 'first-section'
                      : ''
                  }`}
                >
                  <SammanfattningDetails
                    analysName={props.selectedAnalys.analys.name}
                    expandedDetails={props.expandedDetails}
                    setExpandedDetails={props.setExpandedDetails}
                    analysLevel={props.analysLevel}
                    dates={props.settingsForCurrentlyShown.dates}
                    denomination={props.updatedSettings.other.denomination}
                    financialUnit={
                      analysData ? analysData.rapportInfo?.enhet || '' : ''
                    }
                    rubrikNiva={rubrikNiva ?? ''}
                    organisationName={analysData?.rapportInfo?.organisationName}
                    selectedRowHeader={analysData?.selectedRowHeader}
                    tableRows={tableRows}
                    analysType={props.analysType}
                    showChangedCodePrivilegeText={
                      changedCodePrivilege || !hasWholeCodeBook
                    }
                  />
                </EDBox>

                {/* <EDBox className="print-charts-wrapper"> */}
                {analysData && (
                  <EkonomiskAnalysSammanfattningCharts
                    analysData={analysData}
                    analysLevel={props.analysLevel}
                    analysType={props.analysType}
                    availableReports={props.availableReports}
                    currentKoddel={analysData?.selectedRowHeader}
                    expandedBarChart={props.expandedBarChart}
                    expandedLineChart={props.expandedLineChart}
                    expandedPieChart={props.expandedPieChart}
                    setExpandedBarChart={props.setExpandedBarChart}
                    setExpandedLineChart={props.setExpandedLineChart}
                    setExpandedPieChart={props.setExpandedPieChart}
                    expandedResultatDiagram={props.expandedResultatDiagram}
                    setExpandedResultatDiagram={
                      props.setExpandedResultatDiagram
                    }
                    dates={props.settingsForCurrentlyShown.dates}
                    denomination={props.updatedSettings.other.denomination ?? 1}
                    kolumnerRubriker={
                      analysData ? analysData.kolumnerRubriker : []
                    }
                    nrOfDecimals={props.updatedSettings.other.decimals ?? 0}
                    rubrikNiva={rubrikNiva ?? ''}
                    printSections={props.printSections}
                    firstVisibleSection={firstVisibleSection}
                    showFordelningDiagram={props.showFordelningDiagram}
                    showResultatDiagram={props.showResultatDiagram}
                    selectedAnalysCodeprivilegeID={currentCodePrivilegeId}
                    selectedRowHeader={analysData?.selectedRowHeader}
                    subLevelNiva={subLevelNiva}
                    sums={props.originalSettings.sums}
                    urval={props.settingsForCurrentlyShown.urval}
                  />
                )}

                <EDBox
                  data-print-section="tabell"
                  className={`print-section ${
                    !props.printSections.tabell ? 'print-hidden' : ''
                  } ${firstVisibleSection === 'tabell' ? 'first-section' : ''}`}
                >
                  <SammanfattningSubLevelTable
                    analysLevel={props.analysLevel}
                    analysType={props.analysType}
                    availableReports={props.availableReports}
                    denomination={props.updatedSettings.other.denomination}
                    expandedTable={props.expandedTable}
                    setExpandedTable={props.setExpandedTable}
                    kolumnerRubriker={
                      analysData ? analysData.kolumnerRubriker : []
                    }
                    tableRows={tableRows}
                    rubrikNiva={rubrikNiva ? rubrikNiva : ''}
                    subLevelNiva={subLevelNiva}
                    dates={props.settingsForCurrentlyShown.dates}
                    sums={props.originalSettings.sums}
                    enhet={
                      analysData ? analysData.rapportInfo?.enhet || '' : ''
                    }
                    selectedAnalysID={props.selectedAnalys.analys.id}
                    selectedAnalysCodeprivilegeID={currentCodePrivilegeId}
                    urval={props.settingsForCurrentlyShown.urval}
                  />
                </EDBox>
                <EDBox
                  data-print-section="aiInsikter"
                  className={`print-section ${
                    !props.printSections.aiInsikter ? 'print-hidden' : ''
                  } ${
                    firstVisibleSection === 'aiInsikter' ? 'first-section' : ''
                  }`}
                >
                  <SammanfattningInsikterRekommendationer
                    analysType={props.analysType}
                    expandedAIInsights={props.expandedAIInsights}
                    setExpandedAIInsights={props.setExpandedAIInsights}
                    tableRows={tableRows}
                    aiPrompt={promptForAI}
                    aiActiveQueryResult={aiActiveQueryResult}
                  />
                </EDBox>
              </EDBox>
            )}
          </>
        );
      }}
    </EkonomiskAnalysQuery>
  );
};
